import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import { getWorkOrderComponentHistory, updateWorkOrder } from "../../api/work-orderAPI";
import { formatIntDate, formatIntDateTime } from "../../utils/dateUtils";


const getCommentAuthor = (comment) =>
  comment?.createdByUser?.name || comment?.createdByName || comment?.createdBy || "";

const getUserImageKey = (comment) => {
  const user = comment?.createdByUser || {};
  return user.userImage || user.user_image || user.image || null;
};

const getUserInitials = (name) => {
  if (!name) return "";
  const parts = String(name)
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
};

const getTimeValue = (value) => {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  const time = date.getTime();
  return Number.isNaN(time) ? null : time;
};

const ComponentWorkOrdersModal = ({
  show,
  component,
  onClose,
  taskOccurrences,
  workOrderStatuses,
  comments = [],
  commentsLoading = false,
  onCommentCreate,
  onCommentUpdate,
  onCommentDelete,
  canCreateComments = true,
  canEditComments = true,
  canDeleteComments = true,
  onWorkOrderClick,
  formatDate,
}) => {
  const workOrders = useMemo(
    () => (Array.isArray(component?.workOrders) ? component.workOrders : []),
    [component],
  );
  const [workOrderRows, setWorkOrderRows] = useState([]);
  const [updatingId, setUpdatingId] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [workOrderHistory, setWorkOrderHistory] = useState(null);
  const [workOrderHistoryLoading, setWorkOrderHistoryLoading] = useState(false);
  const [historyWorkOrderRows, setHistoryWorkOrderRows] = useState([]);
  const [activeTab, setActiveTab] = useState("occurrences");
  const [showAssetOnly, setShowAssetOnly] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [commentError, setCommentError] = useState("");
  const [commentSaving, setCommentSaving] = useState(false);
  const [deletingCommentId, setDeletingCommentId] = useState(null);
  const [editingComment, setEditingComment] = useState(null);
  const [editingCommentValue, setEditingCommentValue] = useState("");
  const [editCommentSaving, setEditCommentSaving] = useState(false);

  useEffect(() => {
    setWorkOrderRows(
      workOrders.map((workOrder) => ({
        ...workOrder,
        ignoreComponentLife: Number(workOrder?.ignoreComponentLife) === 1,
      })),
    );
    setHasChanges(false);
  }, [workOrders]);

  useEffect(() => {
    if (show) {
      setActiveTab("occurrences");
      setShowAssetOnly(false);
      setNewComment("");
      setCommentError("");
      setCommentSaving(false);
      setDeletingCommentId(null);
      setEditingComment(null);
      setEditingCommentValue("");
      setEditCommentSaving(false);
    }
  }, [show, component?.id]);

  useEffect(() => {
    setHistoryWorkOrderRows(
      Array.isArray(workOrderHistory?.workOrders)
        ? workOrderHistory.workOrders.map((workOrder) => ({
            ...workOrder,
            ignoreComponentLife: Number(workOrder?.ignoreComponentLife) === 1,
          }))
        : [],
    );
  }, [workOrderHistory]);

  const handleIgnoreComponentLifeToggle = async (workOrder, nextValue) => {
    if (!workOrder?.id) {
      return;
    }

    const previousValue = Boolean(workOrder.ignoreComponentLife);
    if (nextValue === previousValue) {
      return;
    }

    setUpdatingId(workOrder.id);
    setWorkOrderRows((prevRows) =>
      prevRows.map((row) =>
        row.id === workOrder.id ? { ...row, ignoreComponentLife: nextValue } : row,
      ),
    );
    setHistoryWorkOrderRows((prevRows) =>
      prevRows.map((row) =>
        row.id === workOrder.id ? { ...row, ignoreComponentLife: nextValue } : row,
      ),
    );

    try {
      await updateWorkOrder(workOrder.id, { ignoreComponentLife: nextValue ? 1 : 0 });
      setHasChanges(true);
    } catch (error) {
      console.error("Failed to update ignoreComponentLife for work order", error);
      setWorkOrderRows((prevRows) =>
        prevRows.map((row) =>
          row.id === workOrder.id ? { ...row, ignoreComponentLife: previousValue } : row,
        ),
      );
      setHistoryWorkOrderRows((prevRows) =>
        prevRows.map((row) =>
          row.id === workOrder.id ? { ...row, ignoreComponentLife: previousValue } : row,
        ),
      );
    } finally {
      setUpdatingId(null);
    }
  };

  const handleClose = () => {
    if (typeof onClose === "function") {
      onClose(hasChanges);
    }
  };

  const formatOccurrenceDate = formatDate || formatIntDate;

  const taskCodeId = component?.codeId ?? component?.code?.id ?? component?.taskCodeId ?? null;
  const taskId = component?.taskId ?? component?.id ?? null;
  const groupId =
    component?.asset?.groupId ?? component?.asset?.group?.id ?? component?.groupId ?? null;
  const componentAssetId = component?.asset?.id ?? component?.assetId ?? null;
  const componentAssetNumber =
    component?.asset?.assetNumber ??
    component?.asset?.unitNumber ??
    component?.assetNumber ??
    component?.unitNumber ??
    null;

  useEffect(() => {
    const fetchWorkOrderHistory = async () => {
      if (!show || !groupId || (!taskCodeId && !taskId)) {
        setWorkOrderHistory(null);
        return;
      }
      setWorkOrderHistoryLoading(true);
      try {
        const response = await getWorkOrderComponentHistory({
          taskCodeIds: taskCodeId ? [taskCodeId] : [],
          taskIds: taskId ? [taskId] : [],
          groupIds: [groupId],
          page: 1,
          pageSize: 100,
        });
        setWorkOrderHistory(response);
      } catch (error) {
        console.error("Error loading work order history", error);
        setWorkOrderHistory(null);
      } finally {
        setWorkOrderHistoryLoading(false);
      }
    };

    fetchWorkOrderHistory();
  }, [groupId, show, taskCodeId, taskId]);

  const formatMetric = (value) => {
    if (value == null || Number.isNaN(Number(value))) {
      return "—";
    }
    return Number(value).toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  };

  const toNumeric = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const isWorkOrderForComponentAsset = (workOrder) => {
    if (!workOrder) {
      return false;
    }

    const workOrderAssetId = workOrder?.assetId ?? workOrder?.asset?.id ?? null;
    if (componentAssetId != null && workOrderAssetId != null) {
      return Number(workOrderAssetId) === Number(componentAssetId);
    }

    const workOrderAssetNumber =
      workOrder?.assetNumber ?? workOrder?.asset?.assetNumber ?? workOrder?.asset?.unitNumber;
    if (componentAssetNumber && workOrderAssetNumber) {
      return (
        String(workOrderAssetNumber).trim().toLowerCase()
        === String(componentAssetNumber).trim().toLowerCase()
      );
    }

    return false;
  };

  const getActualCost = (workOrder) => {
    const costItems = Array.isArray(workOrder?.costItems) ? workOrder.costItems : [];
    if (costItems.length) {
      return costItems.reduce((sum, item) => {
        const partsCost = toNumeric(item?.partsActualCost ?? item?.actualPartsCost);
        const servicesCost = toNumeric(
          item?.servicesActualCost ?? item?.actualServicesCost ?? item?.miscActualCost,
        );
        return sum + partsCost + servicesCost;
      }, 0);
    }

    const partsCost = toNumeric(workOrder?.partsActualCost ?? workOrder?.actualPartsCost);
    const servicesCost = toNumeric(
      workOrder?.servicesActualCost ?? workOrder?.actualServicesCost ?? workOrder?.miscActualCost,
    );
    return partsCost + servicesCost;
  };

  const lastChangedReadingRaw = component?.lastChangedReading;
  const lastChangedReadingNumber =
    lastChangedReadingRaw != null ? Number(lastChangedReadingRaw) : null;
  const lastChangedReading =
    lastChangedReadingNumber != null && !Number.isNaN(lastChangedReadingNumber)
      ? lastChangedReadingNumber
      : null;
  const lastChangedDateTime = useMemo(
    () => getTimeValue(component?.lastChangedDate),
    [component?.lastChangedDate],
  );

  const componentLabel = `${component?.code?.code || ""} ${component?.name || ""}`;
  const measurementUnits = component?.assetMeter?.meter?.measurementUnits?.unitName || "";
  const assetGroupName = component?.asset?.group?.groupName || "";
  const taskName = component?.name || component?.cycleGroupName || "";
  const occurrenceDisplayRows = useMemo(() => {
    const statusMap = new Map(
      (workOrderStatuses || []).map((status) => [String(status.id), status]),
    );

    const scheduledRows = Array.isArray(taskOccurrences?.[0])
      ? taskOccurrences[0].map((occurrence, index) => {
        const status =
          occurrence?.workOrderId === 0
            ? { colours: { bootstrapName: "secondary" } }
            : occurrence?.workOrderStatusId
              ? statusMap.get(String(occurrence.workOrderStatusId))
              : null;
        return {
          key: `occ-${index}`,
          name: occurrence?.name,
          usage: occurrence?.usage != null ? parseInt(occurrence.usage, 10) : null,
          date: occurrence?.timestamp,
          workOrderNumber: occurrence?.workOrderNumber || "",
          workOrder: occurrence.workOrderId,
          statusId: occurrence?.workOrderId === 0 ? 0 : occurrence?.workOrderStatusId || null,
          status,
          statusColor: status?.colours?.bootstrapName || "secondary",
        };
      })
      : [];

    return [...scheduledRows];
  }, [taskOccurrences, workOrderStatuses]);
  const filteredHistoryRows = useMemo(() => {
    if (!showAssetOnly) {
      return historyWorkOrderRows;
    }
    return historyWorkOrderRows.filter((workOrder) =>
      isWorkOrderForComponentAsset(workOrder),
    );
  }, [historyWorkOrderRows, showAssetOnly, componentAssetId, componentAssetNumber]);
  const historyAverages = useMemo(() => {
    if (!Array.isArray(filteredHistoryRows) || filteredHistoryRows.length === 0) {
      return { interval: null, cost: null };
    }

    const eligibleRows = filteredHistoryRows.filter(
      (workOrder) => !workOrder?.ignoreComponentLife,
    );
    if (eligibleRows.length === 0) {
      return { interval: null, cost: null };
    }

    const totals = eligibleRows.reduce(
      (acc, workOrder) => {
        const intervalValue = Number(workOrder?.componentLife);
        if (Number.isFinite(intervalValue)) {
          acc.interval += intervalValue;
          acc.intervalCount += 1;
        }
        acc.cost += getActualCost(workOrder);
        acc.costCount += 1;
        return acc;
      },
      { interval: 0, intervalCount: 0, cost: 0, costCount: 0 },
    );

    return {
      interval: totals.intervalCount ? totals.interval / totals.intervalCount : null,
      cost: totals.costCount ? totals.cost / totals.costCount : null,
    };
  }, [filteredHistoryRows]);

  const occurrenceMeta = taskOccurrences?.[1] ?? {};
  const occurrenceTask = taskOccurrences?.[2] ?? {};
  const occurrenceTitle = occurrenceMeta?.title
    ?.toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase())
    || "Task Occurrences";
  const occurrenceMeasurementUnits = occurrenceMeta?.measurementUnits ?? "";
  const occurrenceRisk = occurrenceTask?.componentRisk;
  const occurrenceOpportunity = occurrenceTask?.componentOpportunity;
  const occurrenceEndLifeRemain = occurrenceTask?.componentEndLifeRemain;
  const occurrenceLifeRatio = Number.isFinite(occurrenceTask?.componentLifeRatio)
    ? parseInt(occurrenceTask.componentLifeRatio * 100, 10)
    : 0;
  const occurrenceFrequency = occurrenceTask?.frequency ?? 0;
  const occurrenceMeterUnits =
    occurrenceTask?.assetMeter?.meter?.measurementUnits?.unitName?.toLowerCase() || "days";
  const occurrenceForecast = occurrenceTask?.forecastOccurrences || 0;
  const occurrenceRiskOpp =
    occurrenceRisk === 1
      ? "Risk"
      : occurrenceOpportunity === 1
        ? "Opportunity"
        : "";
  const occurrenceRiskOppTolerance =
    occurrenceForecast > 0 ? (occurrenceFrequency - occurrenceEndLifeRemain) / occurrenceForecast : 0;
  const occurrenceRiskOppDescription =
    occurrenceRisk === 1
      ? `If future achieved component change frequency is ${parseInt(occurrenceRiskOppTolerance, 10)} ${occurrenceMeterUnits} (${occurrenceLifeRatio}%) below the target, an additional component will need to be changed within the asset life, increasing lifecycle cost.`
      : occurrenceOpportunity === 1
        ? `If you can achieve a future change frequency of ${parseInt(occurrenceRiskOppTolerance, 10)} ${occurrenceMeterUnits} (${100 - occurrenceLifeRatio}%) above the target, one less component change will be needed within the asset life, reducing lifecycle cost.`
        : "";

  const handleSaveComment = async () => {
    const trimmed = newComment.trim();
    if (!trimmed) {
      setCommentError("Comment is required");
      return;
    }
    setCommentError("");
    setCommentSaving(true);
    try {
      await onCommentCreate?.(trimmed);
      setNewComment("");
    } catch (error) {
      console.error("Error creating task comment", error);
    } finally {
      setCommentSaving(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!canDeleteComments || deletingCommentId !== null) return;
    const confirmed = window.confirm("Delete this note?");
    if (!confirmed) return;
    setDeletingCommentId(commentId);
    try {
      await onCommentDelete?.(commentId);
    } catch (error) {
      console.error("Error deleting task comment", error);
    } finally {
      setDeletingCommentId(null);
    }
  };

  const handleUpdateComment = async () => {
    if (!editingComment) return;
    const trimmed = editingCommentValue.trim();
    if (!trimmed) {
      setCommentError("Comment is required");
      return;
    }
    setEditCommentSaving(true);
    try {
      await onCommentUpdate?.(editingComment.id, trimmed);
      setEditingComment(null);
      setEditingCommentValue("");
      setCommentError("");
    } catch (error) {
      console.error("Error updating task comment", error);
    } finally {
      setEditCommentSaving(false);
    }
  };

  if (!show) {
    return null;
  }

  return (
    <div className="modal modal-backdrop show d-block" tabIndex="-1" role="dialog">
      <div className="modal-dialog modal-xl" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              Review Component{componentLabel ? ` - ${componentLabel}` : ""}
            </h5>
            <button type="button" className="btn-close" onClick={handleClose} />
          </div>
          <div className="modal-body">
            <ul className="nav nav-tabs mt-8">
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${activeTab === "occurrences" ? "active" : ""}`}
                  onClick={() => setActiveTab("occurrences")}
                >
                  Occurrences
                </button>
              </li>
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${activeTab === "workOrders" ? "active" : ""}`}
                  onClick={() => setActiveTab("workOrders")}
                >
                  Work Order History
                </button>
              </li>
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${activeTab === "comments" ? "active" : ""}`}
                  onClick={() => setActiveTab("comments")}
                >
                  Comments
                </button>
              </li>
            </ul>
            <div className="mt-16">
              {activeTab === "occurrences" ? (
                <>
                  <h6 className="text-lg mb-16">{occurrenceTitle}</h6>
                  {occurrenceRiskOpp && (
                    <div className="col-md-12">
                      <div
                        className={`alert ${occurrenceRiskOpp === "Risk" ? "alert-danger" : "alert-success"
                          } py-12 d-flex align-items-start`}
                        role="alert"
                      >
                        <Icon
                          icon={
                            occurrenceRiskOpp === "Risk"
                              ? "mdi:alert-circle-outline"
                              : "mdi:lightbulb-on-outline"
                          }
                          className="me-16 mt-1"
                          width="35"
                          height="35"
                        />
                        <span>{occurrenceRiskOppDescription}</span>
                      </div>
                    </div>
                  )}
                  <div className="table-responsive">
                    <table className="table bordered-table table-sm">
                      <thead>
                        <tr>
                          <th>Task Name</th>
                          <th>Work Order #</th>
                          <th>Usage ({occurrenceMeasurementUnits})</th>
                          <th>Date</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {occurrenceDisplayRows.length === 0 && (
                          <tr>
                            <td colSpan="5" className="text-center py-4 rounded-0">
                              No Occurrences Forecast
                            </td>
                          </tr>
                        )}
                        {occurrenceDisplayRows.map((row) => (
                          <tr key={row.key}>
                            <td>{row.name}</td>
                            <td>
                              {row.workOrderNumber ? (
                                <button
                                  type="button"
                                  className="btn btn-link p-0"
                                  onClick={() => onWorkOrderClick?.(row.workOrder)}
                                >
                                  {row.workOrderNumber}
                                </button>
                              ) : (
                                ""
                              )}
                            </td>
                            <td>{row.usage != null ? parseInt(row.usage, 10) : ""}</td>
                            <td>{formatOccurrenceDate(row.date)}</td>
                            <td>
                              {row.statusId !== null && row.status ? (
                                <div
                                  className={`badge bg-${row.statusColor}-focus text-${row.statusColor}`}
                                >
                                  {row.workOrder === 0 ? "Last Done" : row.status.name}
                                </div>
                              ) : (
                                ""
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : activeTab === "comments" ? (
                <>
                  <div className="mb-4">
                    <div className="text-lg fw-bold mb-2">Add Comment</div>
                    <div className="row g-3 align-items-start">
                      <div className="col-12">
                        <textarea
                          id="task-comment-text"
                          className={`form-control ${commentError ? "is-invalid" : ""}`}
                          rows={4}
                          value={newComment}
                          onChange={(event) => {
                            setNewComment(event.target.value);
                            if (commentError) setCommentError("");
                          }}
                          placeholder="Add comment"
                          disabled={!canCreateComments || commentSaving}
                        />
                        {commentError && <div className="invalid-feedback">{commentError}</div>}
                      </div>
                      <div className="col-12 d-flex justify-content-end">
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm"
                          onClick={handleSaveComment}
                          disabled={!canCreateComments || commentSaving}
                        >
                          <Icon icon="mdi:send-outline" className="icon text-xl line-height-1 me-4" />
                          {commentSaving ? "Saving..." : "Post"}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3">
                    <div className="text-lg fw-bold mb-3">Comment History</div>
                    {commentsLoading ? (
                      <div className="text-center py-3">
                        <span className="spinner-border spinner-border-sm text-primary" aria-hidden="true"></span>
                      </div>
                    ) : comments.length === 0 ? (
                      <div className="text-muted">No comments yet.</div>
                    ) : (
                      <div className="d-flex flex-column gap-3" style={{ maxHeight: "440px", overflowY: "auto" }}>
                        {comments.map((comment) => {
                          const userName = getCommentAuthor(comment) || "Unknown User";
                          const timestamp = comment?.createdAt || comment?.created_at;
                          const formattedTimestamp = formatIntDateTime(timestamp);
                          const avatarKey = getUserImageKey(comment);
                          const avatarUrl = avatarKey
                            ? /^https?:/i.test(avatarKey)
                              ? avatarKey
                              : `${process.env.REACT_APP_SPACES_BASE}/${avatarKey}`
                            : null;
                          const initials = getUserInitials(userName);

                          return (
                            <div key={comment.id || `${userName}-${timestamp}`} className="border rounded p-3 d-flex align-items-start gap-3">
                              <div
                                className="flex-shrink-0 d-flex align-items-center justify-content-center rounded-circle bg-primary text-white"
                                style={{ width: "36px", height: "36px", overflow: "hidden" }}
                              >
                                {avatarUrl ? (
                                  <img
                                    src={avatarUrl}
                                    alt={userName}
                                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                  />
                                ) : (
                                  <span className="fw-semibold">{initials}</span>
                                )}
                              </div>
                              <div className="flex-grow-1">
                                <div className="d-flex align-items-start gap-2 flex-wrap">
                                  <div className="d-flex align-items-center gap-2">
                                    <strong>{userName}</strong>
                                    {formattedTimestamp && (
                                      <span className="text-muted text-sm">{formattedTimestamp}</span>
                                    )}
                                  </div>
                                  {(canEditComments || canDeleteComments) && (
                                    <div className="ms-auto d-flex gap-2">
                                      {canEditComments && (
                                        <button
                                          type="button"
                                          className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                          onClick={() => {
                                            setEditingComment(comment);
                                            setEditingCommentValue(comment?.comment || "");
                                            setCommentError("");
                                          }}
                                          disabled={editCommentSaving || commentSaving || deletingCommentId !== null}
                                          title="Edit"
                                        >
                                          <Icon icon="ic:baseline-edit" className="icon text-xl line-height-1" />
                                        </button>
                                      )}
                                      {canDeleteComments && (
                                        <button
                                          type="button"
                                          className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                          onClick={() => handleDeleteComment(comment.id)}
                                          disabled={deletingCommentId === comment.id || editCommentSaving || commentSaving}
                                          title="Delete"
                                        >
                                          {deletingCommentId === comment.id ? (
                                            <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
                                          ) : (
                                            <Icon icon="ic:baseline-delete-outline" className="icon text-xl line-height-1" />
                                          )}
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </div>
                                <div className="mt-2" style={{ whiteSpace: "pre-wrap" }}>
                                  {comment?.comment || ""}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {editingComment && (
                    <div className="mt-3 border rounded p-3 bg-light">
                      <div className="fw-semibold mb-2">Edit Comment</div>
                      <textarea
                        className="form-control"
                        rows={3}
                        value={editingCommentValue}
                        onChange={(event) => setEditingCommentValue(event.target.value)}
                        disabled={editCommentSaving}
                      />
                      <div className="d-flex justify-content-end gap-2 mt-2">
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => {
                            setEditingComment(null);
                            setEditingCommentValue("");
                          }}
                          disabled={editCommentSaving}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={handleUpdateComment}
                          disabled={editCommentSaving}
                        >
                          {editCommentSaving ? "Saving..." : "Save"}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/*<div>
                    <h6 className="text-lg mt-16 mb-24">Work Order History for this Asset</h6>
                  </div>
                  <div className="table-responsive" style={{ maxHeight: "400px" }}>
                    <table className="table bordered-table mb-0 w-100 rounded-0">
                      <thead>
                        <tr>
                          <th>Number</th>
                          <th>Work Order Title</th>
                          <th>Completion Date</th>
                          <th>Target Interval</th>
                          <th>{`Actual Interval In ${measurementUnits}`}</th>
                          <th>Actual Cost</th>
                          <th>Ignore Interval</th>
                        </tr>
                      </thead>
                      <tbody>
                        {workOrderRows.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="text-center py-4">
                              No work orders available for this component.
                            </td>
                          </tr>
                        ) : (
                          workOrderRows.map((workOrder) => {
                            const estimate =
                              workOrder?.workOrderReadingEstimate ??
                              workOrder?.taskReadingEstimate ??
                              null;
                            const estimationFlag = Number(estimate?.estimationFlag) === 1;
                            const rawEstimateValue = estimate?.estimatedValue;
                            const numericEstimateValue =
                              rawEstimateValue != null ? Number(rawEstimateValue) : null;
                            const estimateValue =
                              numericEstimateValue != null && !Number.isNaN(numericEstimateValue)
                                ? numericEstimateValue
                                : null;
                            const workOrderCompletedTime = getTimeValue(workOrder?.completedAt);
                            const matchesReading =
                              estimateValue != null &&
                              lastChangedReading != null &&
                              Math.abs(estimateValue - lastChangedReading) < 0.0001;
                            const matchesDate =
                              lastChangedDateTime != null &&
                              workOrderCompletedTime != null &&
                              workOrderCompletedTime === lastChangedDateTime;
                            const showEstimateBadge =
                              estimationFlag && (matchesReading || matchesDate);

                            return (
                              <tr key={workOrder.id || workOrder.workOrderNumber}>
                                <td>{workOrder.workOrderNumber || ""}</td>
                                <td>{workOrder.workOrderTitle || ""}</td>
                                <td>{formatIntDate(workOrder.completedAt) || ""}</td>
                                <td>{component?.frequency ?? ""}</td>
                                <td>
                                  {Math.round(workOrder.componentLife, 0) || ""}
                                  {showEstimateBadge && (
                                    <span
                                      className="badge bg-primary-200 ms-6"
                                      style={{ position: "relative", top: "-1px" }}>
                                      Est
                                    </span>
                                  )}
                                </td>
                                <td>{formatMetric(getActualCost(workOrder))}</td>
                                <td>
                                  <div className="form-switch switch-primary d-flex align-items-center gap-3 mb-0">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      role="switch"
                                      id={`ignore-component-life-${workOrder.id}`}
                                      checked={Boolean(workOrder.ignoreComponentLife)}
                                      onChange={(event) =>
                                        handleIgnoreComponentLifeToggle(
                                          workOrder,
                                          event.target.checked,
                                        )
                                      }
                                      disabled={updatingId === workOrder.id}
                                      aria-label="Toggle ignore component life"
                                    />
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>*/}
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                    <h6 className="text-lg mb-16">
                      Work Order History for all {assetGroupName} {taskName}
                    </h6>
                    <div className="form-switch switch-primary d-flex align-items-center gap-3  mb-16">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="history-asset-only"
                        checked={showAssetOnly}
                        onChange={(event) => setShowAssetOnly(event.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="history-asset-only">
                        Show Work Orders for this Asset Only                      </label>
                    </div>
                  </div>
                  <div className="table-responsive mb-24" style={{ maxHeight: "400px" }}>
                    <table className="table bordered-table mb-0 w-100 rounded-0">
                      <thead>
                        <tr>
                          <th>Number</th>
                          <th>Work Order Title</th>
                          <th>Asset</th>
                          <th>Completion Date</th>
                          <th>Target Interval</th>
                          <th>{`Interval (${measurementUnits})`}</th>
                          <th>Actual Cost</th>
                          <th>Ignore</th>
                        </tr>
                      </thead>
                      <tbody>
                        {workOrderHistoryLoading ? (
                          <tr>
                            <td colSpan={8} className="text-center py-4">
                              Loading work order history...
                            </td>
                          </tr>
                        ) : filteredHistoryRows.length === 0 ? (
                          <tr>
                            <td colSpan={8} className="text-center py-4">
                              No work orders available for this component history.
                            </td>
                          </tr>
                        ) : (
                          <>
                            {filteredHistoryRows.map((workOrder) => {
                              const isMatch = isWorkOrderForComponentAsset(workOrder);
                              return (
                                <tr
                                  key={workOrder.id || workOrder.workOrderNumber}
                                  className={isMatch ? "text-primary fw-semibold" : ""}
                                >
                                  <td className={`${isMatch ? "bg-primary-focus fw-semibold" : ""} text-end`}>{workOrder.workOrderNumber || ""}</td>
                                  <td className={`${isMatch ? "bg-primary-focus fw-semibold" : ""} text-end`}>{workOrder.workOrderTitle || ""}</td>
                                  <td className={`${isMatch ? "bg-primary-focus fw-semibold" : ""} text-end`}>{workOrder?.assetNumber}</td>
                                  <td className={`${isMatch ? "bg-primary-focus fw-semibold" : ""} text-end`}>{formatIntDate(workOrder.completedAt) || ""}</td>
                                  <td className={`${isMatch ? "bg-primary-focus fw-semibold" : ""} text-end`}>{component?.frequency ?? ""}</td>
                                  <td className={`${isMatch ? "bg-primary-focus fw-semibold" : ""} text-end`}>{Math.round(workOrder.componentLife, 0) || ""}</td>
                                  <td className={`${isMatch ? "bg-primary-focus fw-semibold" : ""} text-end`}>{formatMetric(getActualCost(workOrder))}</td>
                                  <td className={`${isMatch ? "bg-primary-focus fw-semibold" : ""} text-end`}>
                                    <div className="form-switch switch-primary d-flex align-items-center gap-3 mb-0">
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        role="switch"
                                        id={`ignore-component-life-history-${workOrder.id}`}
                                        checked={Boolean(workOrder.ignoreComponentLife)}
                                        onChange={(event) =>
                                          handleIgnoreComponentLifeToggle(
                                            workOrder,
                                            event.target.checked,
                                          )
                                        }
                                        disabled={updatingId === workOrder.id}
                                        aria-label="Toggle ignore component life"
                                      />
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                            <tr className="table-light">
                              <td colSpan={5} className="fw-semibold">
                                Average
                              </td>
                              <td className="fw-bold text-end">{formatMetric(parseInt(historyAverages.interval,10))}</td>
                              <td className="fw-bold text-end">{formatMetric(parseInt(historyAverages.cost,10))}</td>
                              <td />
                            </tr>
                          </>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={handleClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComponentWorkOrdersModal;
